import { useCallback, useEffect, useState } from 'react';
import { Outlet, useLocation, useMatch, useNavigate } from 'react-router-dom';
import CustomerHistoryTab from '../components/CustomerHistoryTab';
import HistoryStatusBar, { HISTORY_STATUSES, type HistoryStatus } from '../components/HistoryStatusBar';
import { supabase } from '../lib/supabase';

/** Passed to /history/repair/:id via <Outlet context>. */
export type HistoryOutletContext = {
  onRepairStatusChanged: () => void;
};

const CustomerHistory = () => {
  const atDetail = !!useMatch('/history/repair/:id');
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<HistoryStatus>('all');
  const [counts, setCounts] = useState<Partial<Record<HistoryStatus, number>>>({});
  const [listRefreshToken, setListRefreshToken] = useState(0);

  const refreshCounts = useCallback(async () => {
    const results = await Promise.all(
      HISTORY_STATUSES.map(async ({ value }) => {
        let query = supabase.from('repairs').select('id', { count: 'exact', head: true });
        if (value !== 'all') query = query.eq('status', value);
        const { count, error } = await query;
        if (error) console.error(`Error counting ${value} repairs:`, error);
        return [value, count ?? 0] as const;
      })
    );
    setCounts(Object.fromEntries(results));
  }, []);

  // Refresh on mount and whenever the user moves between the list and a detail
  // page (a status may have changed there).
  useEffect(() => {
    refreshCounts();
  }, [refreshCounts, pathname]);

  // Auto-refresh when any repair row changes (realtime)
  useEffect(() => {
    const channel = supabase
      .channel('history-repairs-realtime')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'repairs' },
        () => {
          refreshCounts();
          setListRefreshToken((n) => n + 1);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [refreshCounts]);

  // The detail page (Outlet) reports status changes, e.g. a ready repair handed over
  const onRepairStatusChanged = useCallback(() => {
    refreshCounts();
    setListRefreshToken((n) => n + 1);
  }, [refreshCounts]);

  // Picking a status while a repair is open goes back to the (filtered) list
  const handleStatusChange = (status: HistoryStatus) => {
    setStatusFilter(status);
    if (atDetail) navigate('/history');
  };

  return (
    <div className="">
      <HistoryStatusBar value={statusFilter} onChange={handleStatusChange} counts={counts} />
      {/* The list stays mounted while a repair is open (keeps search/scroll for
          "Orqaga"); the repair page takes the full width. */}
      <div className={atDetail ? 'hidden' : undefined}>
        <CustomerHistoryTab statusFilter={statusFilter} refreshToken={listRefreshToken} />
      </div>
      {atDetail && <Outlet context={{ onRepairStatusChanged } satisfies HistoryOutletContext} />}
    </div>
  );
};

export default CustomerHistory;
